import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Upload, GripHorizontal, FileIcon, MoreVertical, GripVertical } from 'lucide-react'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { useMessage } from '../../contexts/MessageContext'
import { useApi } from '../../utils/api'
import { FormLabel, FormInput, FormTextarea, FormSelect, FormButton, CancelButton } from '../../components/forms/FormElements'
import { PageTitleBar } from '../../components/PageTitleBar'
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { AttachmentUploadModal } from '../../components/modals/AttachmentUploadModal'

interface ContentGroup {
  id: number;
  title: string;
}

interface Attachment {
  id?: number;
  s3_key: string;
  name: string;
  fileSize: number;
  fileType?: string;
  sequence: number;
  url?: string;
  height?: number;
  width?: number;
  watermark_top_x_offset?: number;
  watermark_top_y_offset?: number;
  watermark_top_exclude_pages?: string;
  watermark_side_x_offset?: number;
  watermark_side_y_offset?: number;
  watermark_side_exclude_pages?: string;
  content_groups?: ContentGroup[];
  content_group_ids?: number[];
}

interface FormData {
  title: string;
  subject: string;
  html_content: string;
  attachments: Attachment[];
}

interface ApiResponse {
  data: {
    id: number;
    title: string;
    subject: string;
    html_content: string;
    status: string;
    attachments: Attachment[];
    created_at: string;
    updated_at: string;
  };
  success: boolean;
}

export default function EditEmailTemplate() {
  const { id } = useParams<{ id: string }>()
  const { setMessage } = useMessage()
  const { fetchWithAuth } = useApi()
  const [formData, setFormData] = useState<FormData>({
    title: '',
    subject: '',
    html_content: '',
    attachments: [],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null)
  const [editingAttachment, setEditingAttachment] = useState<Attachment | null>(null)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [contentGroups, setContentGroups] = useState<ContentGroup[]>([])
  const navigate = useNavigate()

  useEffect(() => {
    const fetchContentGroups = async () => {
      try {
        const response = await fetchWithAuth('/admin/content-groups');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setContentGroups(data);
          }
        }
      } catch (error) {
        console.error('Error fetching content groups:', error);
      }
    };

    fetchContentGroups();
  }, [fetchWithAuth]);

  const fetchTemplate = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await fetchWithAuth(`/admin/email-templates/${id}`)
      if (!response.ok) {
        throw new Error('Failed to fetch template')
      }
      const apiResponse: ApiResponse = await response.json()
      console.log('Fetched template data:', apiResponse)
      
      if (apiResponse.success && apiResponse.data) {
        setFormData({
          title: apiResponse.data.title || '',
          subject: apiResponse.data.subject || '',
          html_content: apiResponse.data.html_content || '',
          attachments: apiResponse.data.attachments.map(att => ({
            id: att.id,
            s3_key: att.s3_key,
            name: att.name,
            fileSize: att.fileSize,
            fileType: att.fileType,
            sequence: att.sequence,
            url: att.url,
            height: att.height,
            width: att.width,
            watermark_top_x_offset: att.watermark_top_x_offset,
            watermark_top_y_offset: att.watermark_top_y_offset,
            watermark_top_exclude_pages: att.watermark_top_exclude_pages,
            watermark_side_x_offset: att.watermark_side_x_offset,
            watermark_side_y_offset: att.watermark_side_y_offset,
            watermark_side_exclude_pages: att.watermark_side_exclude_pages,
            content_groups: att.content_groups || [],
            content_group_ids: att.content_groups?.map(g => g.id) || []
          }))
        })
      } else {
        throw new Error('Invalid data structure received from API')
      }
    } catch (err) {
      console.error('Error fetching template:', err)
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setIsLoading(false)
    }
  }, [id, fetchWithAuth])

  useEffect(() => {
    fetchTemplate()
  }, [fetchTemplate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prevData => ({ ...prevData, [name]: value }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    console.log('Submitting form data:', formData);

    const transformAttachments = (attachments: Attachment[]) => {
      return attachments.map(attachment => ({
        id: attachment.id,
        s3_key: attachment.s3_key,
        name: attachment.name,
        size: attachment.fileSize,
        type: attachment.fileType,
        sequence: attachment.sequence,
        url: attachment.url,
        height: attachment.height,
        width: attachment.width,
        watermark_top_x_offset: attachment.watermark_top_x_offset || null,
        watermark_top_y_offset: attachment.watermark_top_y_offset || null,
        watermark_top_exclude_pages: attachment.watermark_top_exclude_pages || '',
        watermark_side_x_offset: attachment.watermark_side_x_offset || null,
        watermark_side_y_offset: attachment.watermark_side_y_offset || null,
        watermark_side_exclude_pages: attachment.watermark_side_exclude_pages || '',
        content_group_ids: attachment.content_group_ids || []
      }));
    };

    try {
      const response = await fetchWithAuth(`/admin/email-templates/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.title,
          subject: formData.subject,
          html_content: formData.html_content,
          attachments: transformAttachments(formData.attachments)
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update template');
      }

      const data = await response.json();
      console.log('Template updated:', data);
      setMessage('Template successfully updated!');
      navigate('/email-templates');
    } catch (err) {
      console.error('Error updating template:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttachmentUpload = (attachmentData: Attachment) => {
    console.log('Received attachment data:', attachmentData);
    
    setFormData(prevData => {
      if (editingAttachment) {
        // Editing existing attachment
        return {
          ...prevData,
          attachments: prevData.attachments.map(att => {
            if (att.id === editingAttachment.id) {
              const updatedContentGroups = contentGroups.filter(group => 
                attachmentData.content_group_ids?.includes(group.id)
              );
              return {
                ...attachmentData,
                content_groups: updatedContentGroups
              };
            }
            return att;
          })
        };
      } else {
        // Adding new attachment
        const updatedContentGroups = contentGroups.filter(group => 
          attachmentData.content_group_ids?.includes(group.id)
        );
        return {
          ...prevData,
          attachments: [...prevData.attachments, {
            ...attachmentData,
            content_groups: updatedContentGroups
          }]
        };
      }
    });
    
    setOpenMenuId(null);
    setIsUploadModalOpen(false);
    setEditingAttachment(null);
  };

  const handleRemoveAttachment = async (attachment: Attachment) => {
    console.log("Attempting to delete attachment:", attachment);
    if (!attachment.id) {
      console.error('Attempted to delete attachment with undefined id:', attachment);
      return;
    }
    setDeletingAttachmentId(attachment.id)
    console.log("Set deletingAttachmentId to:", attachment.id);
    try {
      const response = await fetchWithAuth(`/admin/email-templates/attachments/${attachment.id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        throw new Error('Failed to delete attachment')
      }
    } catch (err) {
      console.error('Error deleting attachment:', err)
    } finally {
      setDeletingAttachmentId(null)
      setFormData(prevData => ({ ...prevData, attachments: prevData.attachments.filter((a) => a.id !== attachment.id) }))
    }
  }

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return

    const items = Array.from(formData.attachments)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    setFormData(prevData => ({ ...prevData, attachments: items }))

    try {
      const response = await fetchWithAuth(`/api/email-templates/attachments/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attachmentIds: items.map(item => item.id) }),
      })

      if (!response.ok) {
        throw new Error('Failed to reorder attachments')
      }
    } catch (err) {
      console.error('Error reordering attachments:', err)
    }
  }

  const handleEditAttachment = (attachment: Attachment) => {
    setEditingAttachment(attachment)
    setIsUploadModalOpen(true)
  }

  const handleToggleMenu = (attachmentId: number | undefined) => {
    if (attachmentId === undefined) return;
    setOpenMenuId(openMenuId === attachmentId ? null : attachmentId)
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId !== null && !(event.target as Element).closest('.attachment-menu')) {
        setOpenMenuId(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openMenuId])

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <main className="container mx-auto px-4 py-8">
        <PageTitleBar
          title="Edit Email Template"
          breadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Email Templates', href: '/email-templates' },
            { label: 'Edit Template' },
          ]}
        />

        <div className="bg-white rounded-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <FormLabel htmlFor="title">TITLE</FormLabel>
              <FormInput
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                autoFocus
              />
            </div>

            <div>
              <FormLabel htmlFor="subject">SUBJECT</FormLabel>
              <FormInput
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <FormLabel htmlFor="html_content">HTML CODE</FormLabel>
              <div className="border border-gray-200 rounded-sm">
                <CodeMirror
                  value={formData.html_content}
                  height="400px"
                  extensions={[html()]}
                  theme="light"
                  style={{ fontSize: '14px' }}
                  onChange={(value) => {
                    setFormData(prevData => ({ ...prevData, html_content: value }));
                  }}
                  className="overflow-hidden rounded-sm"
                />
              </div>
            </div>

            <div>
              <div className="flex items-end justify-between mb-4">
                <label className="block text-sm font-semibold text-gray-700 uppercase">ATTACHMENTS</label>
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(true)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-1.5 px-4 rounded-md font-semibold inline-flex items-center text-sm"
                >
                  <Upload className="h-4 w-4 mr-2 text-gray-700" />
                  Upload
                </button>
              </div>

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="attachments-list">
                  {(provided) => (
                    <ul
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="mt-2 space-y-3"
                    >
                      {formData.attachments.length > 0 ? (
                        formData.attachments.map((attachment, index) => (
                          <Draggable
                            key={attachment.s3_key}
                            draggableId={attachment.s3_key}
                            index={index}
                          >
                            {(provided) => (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="bg-white rounded-lg border border-gray-200 overflow-visible relative"
                              >
                                <div className={`p-4 ${!attachment.content_groups?.length ? 'py-3' : ''}`}>
                                  {/* Header with file info and menu */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div {...provided.dragHandleProps} className="mr-3 cursor-move">
                                        <GripVertical className="h-5 w-5 text-[#64748B]" />
                                      </div>
                                      <FileIcon className="h-5 w-5 text-panagora-deep-blue mr-3 flex-shrink-0" />
                                      <span className="text-sm text-[#334155]">
                                        {attachment.name} <span className="text-[#64748B]">({formatFileSize(attachment.fileSize)})</span>
                                      </span>
                                    </div>
                                    <div className="relative attachment-menu">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="text-[#64748B] hover:text-[#334155] hover:bg-gray-100 rounded-lg"
                                        onClick={() => handleToggleMenu(attachment.id)}
                                      >
                                        <MoreVertical className="h-5 w-5" />
                                      </Button>
                                      {openMenuId === attachment.id && (
                                        <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                                          <div className="py-1">
                                            <button
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (attachment.url) {
                                                  window.open(attachment.url, '_blank');
                                                }
                                              }}
                                              className="w-full text-left px-4 py-2 text-sm text-[#334155] hover:bg-gray-100"
                                            >
                                              Download
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEditAttachment(attachment);
                                              }}
                                              className="w-full text-left px-4 py-2 text-sm text-[#334155] hover:bg-gray-100"
                                            >
                                              Edit
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleRemoveAttachment(attachment);
                                              }}
                                              className="w-full text-left px-4 py-2 text-sm text-[#334155] hover:bg-gray-100"
                                              disabled={deletingAttachmentId === attachment.id}
                                            >
                                              {deletingAttachmentId === attachment.id ? (
                                                <div className="flex items-center">
                                                  <div className="w-4 h-4 border-2 border-gray-200 border-t-[#3eaf99] rounded-full animate-spin mr-2"></div>
                                                  Deleting...
                                                </div>
                                              ) : (
                                                'Delete'
                                              )}
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Content Groups */}
                                  {attachment.content_groups && attachment.content_groups.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-3 ml-14">
                                      {attachment.content_groups.map(group => (
                                        <span
                                          key={group.id}
                                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-panagora-deep-blue"
                                        >
                                          {group.title}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </li>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <li className="text-gray-500 text-sm">No attachments uploaded.</li>
                      )}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            <div className="flex justify-end space-x-3">
              <CancelButton type="button" onClick={() => navigate('/email-templates')}>
                Cancel
              </CancelButton>
              <FormButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Updating...' : 'Update'}
              </FormButton>
            </div>
            {error && <div className="text-red-500 mt-2">{error}</div>}
          </form>
        </div>
      </main>

      <AttachmentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false)
          setEditingAttachment(null)
        }}
        onSubmit={handleAttachmentUpload}
        editingAttachment={editingAttachment}
      />
    </div>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatPages(pages: string | undefined): string {
  return pages ? pages : 'None';
}
